import React, { Suspense } from 'react';
import { CircularProgress, Box } from '@mui/material';

// Loading component
const LoadingSpinner: React.FC = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
    sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}
  >
    <CircularProgress />
  </Box>
);

// Lazy load heavy components
export const LazyProjectDetails = React.lazy(() => import('./ProjectDetails'));
export const LazyCurrentChangesView = React.lazy(() => import('./CurrentChangesView'));
export const LazyChangedFilesList = React.lazy(() => import('./ChangedFilesList'));
export const LazyApplicationFoldersList = React.lazy(() => import('./ApplicationFoldersList'));
export const LazyFolderGitHistory = React.lazy(() => import('./FolderGitHistory'));
export const LazySystemParametersGitHistory = React.lazy(() => import('./SystemParametersGitHistory'));
export const LazyParametersView = React.lazy(() => import('./ParametersView'));

// HOC for wrapping lazy components with Suspense
export const withSuspense = <P extends object>(
  Component: React.ComponentType<P>
) => {
  const WrappedComponent: React.FC<P> = (props) => (
    <Suspense fallback={<LoadingSpinner />}>
      <Component {...props} />
    </Suspense>
  );
  
  WrappedComponent.displayName = `withSuspense(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

// Pre-wrapped lazy components
export const ProjectDetails = withSuspense(LazyProjectDetails);
export const CurrentChangesView = withSuspense(LazyCurrentChangesView);
export const ChangedFilesList = withSuspense(LazyChangedFilesList);
export const ApplicationFoldersList = withSuspense(LazyApplicationFoldersList);
export const FolderGitHistory = withSuspense(LazyFolderGitHistory);
export const SystemParametersGitHistory = withSuspense(LazySystemParametersGitHistory);
export const ParametersView = withSuspense(LazyParametersView);
